import { X } from "lucide-react";
import React, { type ReactNode, useEffect, useState } from "react";

import {
  Box,
  debounce,
  IconButton,
  Stack,
  SwipeableDrawer,
  type SwipeableDrawerProps,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { IgTypography } from "../IgTypography";

const Puller = styled("div")(({ theme }) => ({
  width: 30,
  height: 6,
  backgroundColor: theme.igPrimitives.gray["A100"],
  borderRadius: 3,
  position: "absolute",
  top: 8,
  left: "calc(50% - 15px)",
}));

const HIDE_SCROLLBAR_STYLES = {
  "&::-webkit-scrollbar": {
    display: "none",
  },
  scrollbarWidth: "none",
  msOverflowStyle: "none",
};

export default function IgMobileDrawer({
  children,
  header,
  headerLeftAdornment,
  maxHeight = "90svh",
  onClose,
  sx,
  ...rest
}: SwipeableDrawerProps & {
  header?: ReactNode;
  headerLeftAdornment?: ReactNode;
  maxHeight?: string;
}) {
  const iOS =
    typeof navigator !== "undefined" &&
    /iPad|iPhone|iPod/.test(navigator.userAgent);

  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) {
      return;
    }

    const handleResize = debounce(() => {
      const height = window.innerHeight - viewport.height;
      setKeyboardHeight(height > 0 ? height : 0);
    });

    viewport.addEventListener("resize", handleResize);
    return () => {
      handleResize.clear();
      viewport.removeEventListener("resize", handleResize);
    };
  }, []);

  const headerContent = (
    <Stack
      direction="row"
      alignItems="center"
      sx={{ position: "relative", width: "100%" }}
    >
      {headerLeftAdornment && (
        <Box sx={{ alignSelf: "flex-start" }}>{headerLeftAdornment}</Box>
      )}
      <IgTypography
        sx={{
          fontWeight: 600,
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%)",
          fontSize: 17,
        }}
      >
        {header}
      </IgTypography>
      {onClose && (
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            top: -4,
            borderRadius: 99,
            marginLeft: "auto",
            backgroundColor: (theme) => theme.palette.grey[500],
            "&:hover": {
              backgroundColor: (theme) => theme.palette.grey[700],
            },
          }}
        >
          <X size={16} />
        </IconButton>
      )}
    </Stack>
  );

  return (
    <SwipeableDrawer
      anchor="bottom"
      disableBackdropTransition={!iOS}
      disableDiscovery={iOS}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          padding: 4,
          maxHeight:
            keyboardHeight > 0
              ? `calc(${maxHeight} - ${keyboardHeight + 32}px)`
              : maxHeight,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          bottom: keyboardHeight,
          paddingBottom: "max(env(safe-area-inset-bottom), 20px)",
        },
        ...sx,
      }}
      {...rest}
    >
      <Stack spacing={2} sx={{ minHeight: 200 }}>
        <Stack id="mobile-drawer-header" spacing={2}>
          <Puller />
          {headerContent}
        </Stack>
        <Stack
          id="mobile-drawer-content"
          sx={{
            flex: 1,
            overflowY: "auto",
            minHeight: 0,
            ...HIDE_SCROLLBAR_STYLES,
          }}
        >
          {children}
        </Stack>
      </Stack>
    </SwipeableDrawer>
  );
}
